/**
 * The candidate seam's own error type.
 *
 * Screens branch on a `CandidateApiError` and never touch the transport's `ApiError` directly, so
 * the mock and the HTTP client raise the same shape and a screen's error handling is identical
 * against either backing. `code` is the backend's coarse error string (`{ error: "..." }` —
 * `AuthError`/`RequestError`), deliberately non-specific so verify/refresh failures never become an
 * account or code oracle (see the API's endpoint comments).
 */
export type CandidateErrorKind =
  | 'network'
  | 'server'
  | 'unauthorized'
  | 'rate_limited'
  | 'conflict'
  /** 403 — the content chokepoint's deny for a locked/not-yet-entitled read (letter, PDF). */
  | 'forbidden'
  /** 404 — a request id that is not the candidate's (and not-real folds into the same answer). */
  | 'not_found';

export class CandidateApiError extends Error {
  readonly kind: CandidateErrorKind;
  readonly status: number;
  readonly code?: string;
  /**
   * Seconds until the caller may retry, lifted from the response `Retry-After` header the server
   * sends on every 429 (rate-limit / cooldown). Present only on rate-limited failures; lets copy say
   * *how long* rather than a vague "wait a moment". Undefined when there is no header to trust.
   */
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    kind: CandidateErrorKind,
    status: number,
    code?: string,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'CandidateApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}
